import { ApiResponse as APIResponse } from '../../shared/types';
import {
  CreateDepartmentRequest,
  Department,
  UpdateDepartmentRequest,
} from '../types/department';
import {
  Addon,
  CreateAddonRequest,
  CreateCategoryRequest,
  CreateMenuItemRequest,
  FilterMenuItemsParams,
  MenuCategory,
  MenuItem,
  UpdateAddonRequest,
  UpdateCategoryRequest,
  UpdateMenuItemRequest,
} from '../types/menu';
import { supabase } from './supabase';

const mapDepartmentRecord = (value: Record<string, unknown>): Department => {
  const outlet = value.outlets as { name?: string } | null;
  const stockLocation = value.inventory_stock_locations as {
    name?: string;
  } | null;

  return {
    ...(value as unknown as Department),
    outlet_name: outlet?.name,
    stock_location_name: stockLocation?.name,
  };
};

const mapCategoryRecord = (value: Record<string, unknown>): MenuCategory => {
  const outlet = value.outlets as { name?: string } | null;

  return {
    ...(value as unknown as MenuCategory),
    outlet_name: outlet?.name,
  };
};

const mapMenuItemRecord = (value: Record<string, unknown>): MenuItem => {
  const menuCategories = value.menu_categories as { name?: string } | null;
  const departments = value.departments as { name?: string } | null;
  const outlet = value.outlets as { name?: string } | null;

  return {
    ...(value as unknown as MenuItem),
    outlet_name: outlet?.name,
    category_name: menuCategories?.name,
    department_name: departments?.name,
    menu_categories: undefined,
    departments: undefined,
    outlets: undefined,
  } as unknown as MenuItem;
};

const mapAddonRecord = (value: Record<string, unknown>): Addon => {
  const outlet = value.outlets as { name?: string } | null;

  return {
    ...(value as unknown as Addon),
    outlet_name: outlet?.name,
  };
};

/* Departments API Service */

export const getCompanyDepartments = async (
  outletId?: string,
): Promise<APIResponse<Department[]>> => {
  try {
    const query = supabase
      .from('departments')
      .select('*, outlets(name), inventory_stock_locations(name)')
      .order('name', { ascending: true });
    if (outletId) {
      query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }

    return {
      success: true,
      data: (data || []).map((row) =>
        mapDepartmentRecord(row as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch departments',
      data: [],
    };
  }
};

export const createCompanyDepartment = async (
  department: CreateDepartmentRequest,
): Promise<APIResponse<Department>> => {
  try {
    const { data, error } = await supabase
      .from('departments')
      .insert({
        outlet_id: department.outlet_id,
        name: department.name,
        address: department.address || null,
        status: department.status || 'active',
        kot_enabled: department.kot_enabled ?? true,
        display_order: department.display_order || 0,
        stock_location_id: department.stock_location_id || null,
      })
      .select('*, outlets(name), inventory_stock_locations(name)')
      .single();

    if (error) {
      return {
        success: false,
        error: error.message,
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapDepartmentRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to create department',
      data: undefined,
    };
  }
};

export const updateDepartment = async (
  department: UpdateDepartmentRequest,
): Promise<APIResponse<Department>> => {
  try {
    const updateData: Partial<Department> = {};
    if (department.name !== undefined) updateData.name = department.name;
    if (department.address !== undefined)
      updateData.address = department.address;
    if (department.status !== undefined) updateData.status = department.status;
    if (department.kot_enabled !== undefined)
      updateData.kot_enabled = department.kot_enabled;
    if (department.display_order !== undefined)
      updateData.display_order = department.display_order;
    if (department.stock_location_id !== undefined)
      updateData.stock_location_id = department.stock_location_id;

    const { data, error } = await supabase
      .from('departments')
      .update(updateData)
      .eq('id', department.id)
      .select('*, outlets(name), inventory_stock_locations(name)')
      .single();

    if (error) {
      return {
        success: false,
        error: error.message,
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapDepartmentRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update department',
      data: undefined,
    };
  }
};

export const deleteDepartment = async (
  departmentId: string,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase
      .from('departments')
      .delete()
      .eq('id', departmentId);

    if (error) {
      return {
        success: false,
        error: error.message,
        data: undefined,
      };
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to delete department',
      data: undefined,
    };
  }
};

/* Menu Categories API Service */
export const fetchOutletCategories = async (
  outletId?: string | null,
): Promise<APIResponse<MenuCategory[]>> => {
  try {
    let query = supabase
      .from('menu_categories')
      .select('*, outlets(name)')
      .order('display_order', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: (data || []).map((row) =>
        mapCategoryRecord(row as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch menu categories',
      data: [],
    };
  }
};

export const createMenuCategory = async (
  category: CreateCategoryRequest,
): Promise<APIResponse<MenuCategory>> => {
  try {
    const { data, error } = await supabase
      .from('menu_categories')
      .insert({
        outlet_id: category.outlet_id,
        name: category.name,
        description: category.description || null,
        display_order: category.display_order || 0,
        status: category.status || 'active',
      })
      .select('*, outlets(name)')
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: mapCategoryRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create menu category',
      data: undefined,
    };
  }
};

export const updateMenuCategory = async (
  category: UpdateCategoryRequest,
): Promise<APIResponse<MenuCategory>> => {
  try {
    const updateData: Partial<MenuCategory> = {};
    if (category.name !== undefined) updateData.name = category.name;
    if (category.description !== undefined)
      updateData.description = category.description;
    if (category.display_order !== undefined)
      updateData.display_order = category.display_order;
    if (category.status !== undefined) updateData.status = category.status;

    const { data, error } = await supabase
      .from('menu_categories')
      .update(updateData)
      .eq('id', category.id)
      .select('*, outlets(name)')
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: mapCategoryRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update menu category',
      data: undefined,
    };
  }
};

export const deleteMenuCategory = async (
  categoryId: string,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase
      .from('menu_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete menu category',
      data: undefined,
    };
  }
};

/* Menu Items API Service */

export const fetchOutletMenuItems = async ({
  outletId,
  categoryId,
  itemName,
}: FilterMenuItemsParams): Promise<APIResponse<MenuItem[]>> => {
  try {
    // TODO:: Create a View for menu items with their category and department names

    const query = supabase.from('menu_items').select(
      `
              *,
              outlets(name),
              menu_categories(name),
              departments(name)
            `,
    );

    if (outletId) {
      query.eq('outlet_id', outletId);
    }

    if (categoryId) {
      query.eq('category_id', categoryId);
    }

    if (itemName) {
      query.ilike('name', `%${itemName}%`);
    }

    const { data, error } = await query.order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    // Transform the response to flatten joined fields
    const items = (data || []).map((item: Record<string, unknown>) =>
      mapMenuItemRecord(item),
    );

    return { success: true, data: items };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch menu items',
      data: undefined,
    };
  }
};

export const createOutletMenuItem = async (
  request: CreateMenuItemRequest,
): Promise<APIResponse<MenuItem>> => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .insert({
        outlet_id: request.outlet_id,
        category_id: request.category_id || null,
        department_id: request.department_id || null,
        name: request.name,
        description: request.description || null,
        base_price: request.base_price,
        image_url: request.image_url || null,
        sku: request.sku || null,
        status: request.status || 'active',
        display_order: request.display_order || 0,
        is_taxable: request.is_taxable ?? true,
        tax_rate: request.tax_rate || 0,
        preparation_time: request.preparation_time || null,
        calories: request.calories || null,
        allergens: request.allergens || null,
      })
      .select(
        `
              *,
              outlets(name),
              menu_categories(name),
              departments(name)
            `,
      )
      .single();

    if (error) {
      return {
        success: false,
        error: error.message,
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapMenuItemRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to create menu item',
      data: undefined,
    };
  }
};

export const updateOutletMenuItem = async (
  request: UpdateMenuItemRequest,
): Promise<APIResponse<MenuItem>> => {
  try {
    const updateData: Partial<MenuItem> = {};
    if (request.category_id !== undefined)
      updateData.category_id = request.category_id;
    if (request.department_id !== undefined)
      updateData.department_id = request.department_id;
    if (request.name !== undefined) updateData.name = request.name;
    if (request.description !== undefined)
      updateData.description = request.description;
    if (request.base_price !== undefined)
      updateData.base_price = request.base_price;
    if (request.image_url !== undefined)
      updateData.image_url = request.image_url;
    if (request.sku !== undefined) updateData.sku = request.sku;
    if (request.status !== undefined) updateData.status = request.status;
    if (request.display_order !== undefined)
      updateData.display_order = request.display_order;
    if (request.is_taxable !== undefined)
      updateData.is_taxable = request.is_taxable;
    if (request.tax_rate !== undefined) updateData.tax_rate = request.tax_rate;
    if (request.fiscal_tax_category_id !== undefined)
      updateData.fiscal_tax_category_id = request.fiscal_tax_category_id;
    if (request.ebm_item_cd !== undefined)
      updateData.ebm_item_cd = request.ebm_item_cd;
    if (request.ebm_item_cls_cd !== undefined)
      updateData.ebm_item_cls_cd = request.ebm_item_cls_cd;
    if (request.preparation_time !== undefined)
      updateData.preparation_time = request.preparation_time;
    if (request.calories !== undefined) updateData.calories = request.calories;
    if (request.allergens !== undefined)
      updateData.allergens = request.allergens;

    const { data, error } = await supabase
      .from('menu_items')
      .update(updateData)
      .eq('id', request.id)
      .select(
        `
              *,
              outlets(name),
              menu_categories(name),
              departments(name)
            `,
      )
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: mapMenuItemRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update menu item',
      data: undefined,
    };
  }
};

export const deleteOutletMenuItem = async (
  itemId: string,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase
      .from('menu_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to delete menu item',
      data: undefined,
    };
  }
};

/* Addon Items API Service */
export const getOutletAddons = async (
  outletId?: string | null,
): Promise<APIResponse<Addon[]>> => {
  try {
    let query = supabase
      .from('addon_library')
      .select('*, outlets(name)')
      .order('name', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: (data || []).map((row) =>
        mapAddonRecord(row as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch addons',
      data: [],
    };
  }
};

export const createOutletAddon = async (
  request: CreateAddonRequest,
): Promise<APIResponse<Addon>> => {
  try {
    const { data, error } = await supabase
      .from('addon_library')
      .insert({
        outlet_id: request.outlet_id,
        name: request.name,
        default_price: request.default_price,
        status: request.status || 'active',
      })
      .select('*, outlets(name)')
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: mapAddonRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create addon',
      data: undefined,
    };
  }
};

export const updateOutletAddon = async (
  request: UpdateAddonRequest,
): Promise<APIResponse<Addon>> => {
  try {
    const updateData: Partial<Addon> = {};
    if (request.name !== undefined) updateData.name = request.name;
    if (request.default_price !== undefined)
      updateData.default_price = request.default_price;
    if (request.status !== undefined) updateData.status = request.status;

    const { data, error } = await supabase
      .from('addon_library')
      .update(updateData)
      .eq('id', request.id)
      .select('*, outlets(name)')
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: mapAddonRecord(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update addon',
      data: undefined,
    };
  }
};

export const deleteOutletAddon = async (
  addonId: string,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase
      .from('addon_library')
      .delete()
      .eq('id', addonId);

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete addon',
      data: undefined,
    };
  }
};

export interface EbmItemCdPrefixParts {
  originNationCd: string;
  productTypeCd: string;
  pkgUnitCd: string;
  qtyUnitCd: string;
}

/**
 * Suggests the next unused sequence number for a given Origin+ProductType+
 * PkgUnit+QtyUnit combination among this outlet's already-classified menu
 * items. The 7-char prefix (2+1+2+2) unambiguously identifies the
 * combination since a valid ebm_item_cd is always exactly 14 characters —
 * a LIKE 'PREFIX%' match is sufficient, no per-row parsing needed.
 */
export const fetchNextEbmItemSequence = async (
  outletId: string,
  parts: EbmItemCdPrefixParts,
): Promise<number> => {
  const prefix = `${parts.originNationCd}${parts.productTypeCd}${parts.pkgUnitCd}${parts.qtyUnitCd}`;
  const { data, error } = await supabase
    .from('menu_items')
    .select('ebm_item_cd')
    .eq('outlet_id', outletId)
    .like('ebm_item_cd', `${prefix}%`);

  if (error) {
    throw new Error(error.message);
  }

  const sequences = (data ?? [])
    .map((row) => row.ebm_item_cd as string | null)
    .filter((cd): cd is string => cd !== null && cd.length === 14)
    .map((cd) => Number.parseInt(cd.slice(7, 14), 10))
    .filter((n) => Number.isFinite(n));

  const max = sequences.length ? Math.max(...sequences) : 0;
  return Math.min(max + 1, 9_999_999);
};
